import React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  LEAVE_REQUEST_STATUS_COLORS,
  LEAVE_REQUEST_STATUS_LABELS,
  LEAVE_TYPE_COLORS,
  LEAVE_TYPE_LABELS,
} from '../../containers/attendancemng/AttendMngContainer';

const STEP_STATUS_LABELS = {
  WAITING: '대기',
  PENDING: '처리대기',
  APPROVED: '승인',
  REJECTED: '반려',
  CANCELED: '취소',
  SKIPPED: '생략',
  DELEGATED: '대결',
};

const ACTION_TYPE_LABELS = {
  SUBMIT: '상신',
  APPROVE: '승인',
  REJECT: '반려',
  RECALL: '회수',
  CANCEL: '취소',
  SKIP: '생략',
  DELEGATE_APPLY: '대결적용',
  AUTO_APPROVE: '자동승인',
  LINE_CHANGED: '결재선변경',
};

const LeaveRequestDetailDialog = ({ open, loading, detail, onClose }) => {
  const info = detail?.info;
  const steps = detail?.steps || [];
  const actions = detail?.actions || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>휴가 신청 상세</DialogTitle>
      <DialogContent dividers>
        {!info ? (
          <Typography variant="body2" color="text.secondary">
            {loading ? '상세 정보를 불러오는 중입니다.' : '표시할 휴가 신청 정보가 없습니다.'}
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>{info.reqNo}</Typography>
                <Chip
                  label={LEAVE_REQUEST_STATUS_LABELS[info.reqStatus] || info.reqStatus}
                  color={LEAVE_REQUEST_STATUS_COLORS[info.reqStatus] || 'default'}
                  size="small"
                />
                <Chip
                  label={LEAVE_TYPE_LABELS[info.leaveTyp] || info.leaveTyp}
                  color={LEAVE_TYPE_COLORS[info.leaveTyp] || 'default'}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">신청자</Typography>
                  <Typography variant="body2">{info.usrNm} {info.dptNm ? `(${info.dptNm})` : ''}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">현재 결재자</Typography>
                  <Typography variant="body2">{info.currentActorNm || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">기간</Typography>
                  <Typography variant="body2">
                    {dayjs(info.leaveDtSt).format('YYYY-MM-DD')} ~ {dayjs(info.leaveDtEd).format('YYYY-MM-DD')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">일수</Typography>
                  <Typography variant="body2">{info.leaveDays}일</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">상신일</Typography>
                  <Typography variant="body2">
                    {info.submittedAt ? dayjs(info.submittedAt).format('YYYY-MM-DD HH:mm') : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">최종 반영 근태 UUID</Typography>
                  <Typography variant="body2">{info.finalLeaveUuid || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">사유</Typography>
                  <Typography variant="body2">{info.rmk || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                결재 단계
              </Typography>
              <List dense disablePadding>
                {steps.map((step) => (
                  <ListItem key={step.apprDocStepId} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={(
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {step.stepOrder}차 결재
                          </Typography>
                          <Chip
                            label={STEP_STATUS_LABELS[step.stepStatus] || step.stepStatus}
                            size="small"
                            color={step.stepStatus === 'APPROVED' ? 'success' : step.stepStatus === 'REJECTED' ? 'error' : 'default'}
                            variant={step.stepStatus === 'PENDING' ? 'filled' : 'outlined'}
                          />
                        </Stack>
                      )}
                      secondary={(
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            원결재자: {step.originalAssigneeNm || step.originalAssigneeUsrUuid || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            현재 대상자: {step.currentAssigneeNm || step.currentAssigneeUsrUuid || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            처리자: {step.actorUsrNm || '-'}
                            {step.actedAt ? ` / ${dayjs(step.actedAt).format('YYYY-MM-DD HH:mm')}` : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            의견: {step.actionComment || '-'}
                          </Typography>
                        </Stack>
                      )}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                액션 이력
              </Typography>
              <List dense disablePadding>
                {actions.map((action) => (
                  <ListItem key={action.actionId} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={(
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={ACTION_TYPE_LABELS[action.actionType] || action.actionType}
                            size="small"
                            color={action.actionType === 'APPROVE' ? 'success' : action.actionType === 'REJECT' ? 'error' : 'default'}
                            variant="outlined"
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {action.actorUsrNm || action.actorUsrUuid || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.actionAt ? dayjs(action.actionAt).format('YYYY-MM-DD HH:mm') : ''}
                          </Typography>
                        </Stack>
                      )}
                      secondary={(
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            의견: {action.actionComment || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            처리구분: {action.onBehalfOfUsrNm ? `${action.onBehalfOfUsrNm} 대결 처리` : '본결재'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            스냅샷: v{action.docSnapshotVer || '-'} / {action.docSnapshotHash || '-'}
                          </Typography>
                        </Stack>
                      )}
                    />
                  </ListItem>
                ))}
                {actions.length === 0 && (
                  <Typography variant="body2" color="text.secondary">액션 이력이 없습니다.</Typography>
                )}
              </List>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveRequestDetailDialog;
